import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { Event } from '@buzzmap/shared';
import { categoryColors, categoryLabels, formatDateRange, trackEvent } from '../../utils';
import { EventLink } from '../events/EventLink';
import { EventCoverImage } from './EventCoverImage';
import { useDiscoveryNav } from '../../hooks/useDiscoveryNav';
import styles from './SwipeableEventCards.module.css';

interface SwipeableEventCardsProps {
  events: Event[];
  loading: boolean;
  /**
   * T105-FE: la prima carta della pila è il candidato LCP della vista iniziale (eager +
   * fetchpriority high). Default false: la imposta solo chi sa che la carta è visibile senza
   * scorrere; le carte dietro restano sempre lazy.
   */
  prioritizeFirstCard?: boolean;
}

/** Larghezza reale della carta: la colonna (max 400 px da 768 px) meno il padding della sezione. */
const COVER_SIZES = '(min-width: 768px) 400px, calc(100vw - 32px)';

/** Minimum horizontal distance (px) to trigger a swipe action */
const SWIPE_THRESHOLD = 60;
/** Minimum velocity (px/ms) to trigger a swipe even below distance threshold */
const VELOCITY_THRESHOLD = 0.4;
/** How far the card flies off screen (px) */
const FLY_OFF_DISTANCE = 500;
/** Max visible cards in the stack behind the active card */
const VISIBLE_STACK = 3;

interface DragState {
  startX: number;
  startY: number;
  startTime: number;
  currentX: number;
  isDragging: boolean;
  /** True once we've determined this gesture is primarily horizontal */
  isHorizontal: boolean | null;
}

const INITIAL_DRAG: DragState = {
  startX: 0,
  startY: 0,
  startTime: 0,
  currentX: 0,
  isDragging: false,
  isHorizontal: null,
};

export function SwipeableEventCards({
  events,
  loading,
  prioritizeFirstCard = false,
}: SwipeableEventCardsProps) {
  const navigate = useNavigate();
  const location = useLocation();
  // T013: the empty-state CTA targets the Esplora surface of the current URL
  // through the shared builder in v2 (territorial anchoring), `/esplora` in
  // legacy.
  const nav = useDiscoveryNav();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dragDelta, setDragDelta] = useState(0);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [flyDirection, setFlyDirection] = useState<'left' | 'right' | null>(null);
  const dragRef = useRef<DragState>({ ...INITIAL_DRAG });
  const cardRef = useRef<HTMLDivElement>(null);
  const animTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // The pointer handlers decide what a gesture means (tap opens, drag does
  // not); the browser still dispatches a click on the title link after
  // pointerup. This flag swallows exactly that trailing click, so a drag can
  // never open the event and a tap never opens it twice. Reset when a new
  // gesture starts, consumed by the link's action.
  const swallowClickRef = useRef(false);

  // Cleanup animation timer on unmount to prevent spurious navigation
  useEffect(() => {
    return () => {
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
    };
  }, []);

  const remaining = events.length - currentIndex;

  const advanceCard = useCallback(
    (direction: 'left' | 'right') => {
      setFlyDirection(direction);
      setIsAnimatingOut(true);

      // If swiped right, navigate to the event after animation
      const event = events[currentIndex];
      trackEvent('card_swipe', {
        direction,
        event_title: event?.title ?? '',
        event_id: event?.id ?? '',
      });

      animTimerRef.current = setTimeout(() => {
        if (direction === 'right' && event) {
          // T014: backgroundLocation for desktop modal overlay (no-op on mobile).
          navigate(`/event/${event.id}`, { state: { backgroundLocation: location } });
        }
        setCurrentIndex((prev) => prev + 1);
        setIsAnimatingOut(false);
        setFlyDirection(null);
        setDragDelta(0);
      }, 300);
    },
    [currentIndex, events, navigate, location]
  );

  // --- Touch handlers ---

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (isAnimatingOut) return;
      const el = e.currentTarget as HTMLElement;
      el.setPointerCapture(e.pointerId);
      // A new gesture starts: no stale swallow may eat its trailing click.
      swallowClickRef.current = false;
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startTime: Date.now(),
        currentX: e.clientX,
        isDragging: true,
        isHorizontal: null,
      };
      setDragDelta(0);
    },
    [isAnimatingOut]
  );

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag.isDragging) return;

    const deltaX = e.clientX - drag.startX;
    const deltaY = e.clientY - drag.startY;

    // Determine gesture direction on first significant move
    if (drag.isHorizontal === null) {
      const absDx = Math.abs(deltaX);
      const absDy = Math.abs(deltaY);
      if (absDx > 8 || absDy > 8) {
        drag.isHorizontal = absDx > absDy * 0.6;
        if (!drag.isHorizontal) {
          // Vertical scroll -- abort drag. The browser may still dispatch a
          // click afterwards (no scroll happened): swallow it — a scroll
          // gesture must never open the event.
          drag.isDragging = false;
          swallowClickRef.current = true;
          setDragDelta(0);
          return;
        }
      } else {
        return; // Not enough movement to determine direction
      }
    }

    drag.currentX = e.clientX;
    setDragDelta(deltaX);
  }, []);

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current;
      if (!drag.isDragging) return;
      drag.isDragging = false;
      // The gesture has had its say: swallow the browser's trailing click
      // (see swallowClickRef) — the tap below already opened, a drag must not.
      swallowClickRef.current = true;

      const deltaX = e.clientX - drag.startX;
      const elapsed = Date.now() - drag.startTime;
      const velocity = Math.abs(deltaX) / Math.max(elapsed, 1);

      // Tap detection: minimal movement + short duration = navigate to event.
      // Modified / non-primary clicks are skipped: with the title link the
      // browser opens a new tab natively (same as anywhere else on a link).
      const isModified = e.ctrlKey || e.metaKey || e.shiftKey || e.altKey;
      if (
        !isModified &&
        e.button === 0 &&
        Math.abs(deltaX) < 8 &&
        elapsed < 300 &&
        drag.isHorizontal !== false
      ) {
        const event = events[currentIndex];
        if (event) {
          trackEvent('card_tap', { event_id: event.id, event_title: event.title });
          // T014: backgroundLocation for desktop modal overlay (no-op on mobile).
          navigate(`/event/${event.id}`, { state: { backgroundLocation: location } });
        }
        return;
      }

      const pastThreshold = Math.abs(deltaX) > SWIPE_THRESHOLD || velocity > VELOCITY_THRESHOLD;

      if (pastThreshold && drag.isHorizontal) {
        advanceCard(deltaX > 0 ? 'right' : 'left');
      } else {
        // Spring back
        setDragDelta(0);
      }
    },
    [advanceCard, events, currentIndex, navigate, location]
  );

  const handlePointerCancel = useCallback(() => {
    dragRef.current.isDragging = false;
    swallowClickRef.current = true;
    setDragDelta(0);
  }, []);

  // --- Loading skeleton ---
  if (loading) {
    return (
      <section className={styles.section} aria-label="Imperdibili">
        <h2 className={styles.heading}>Imperdibili</h2>
        <div className={styles.stackContainer}>
          <div className={styles.skeletonCard} aria-busy="true" />
        </div>
      </section>
    );
  }

  // --- All swiped ---
  if (remaining <= 0) {
    return (
      <section className={styles.section} aria-label="Imperdibili">
        <h2 className={styles.heading}>Imperdibili</h2>
        <div className={styles.emptyState}>
          <p className={styles.emptyText}>Hai visto tutti gli eventi!</p>
          <button
            type="button"
            className={styles.exploreCta}
            onClick={() => navigate(nav.esploraUrl)}
          >
            Esplora tutti gli eventi &rarr;
          </button>
        </div>
      </section>
    );
  }

  // Calculate rotation (subtle tilt while dragging)
  const rotation = dragDelta * 0.06; // degrees per pixel

  // Opacity of swipe indicators
  const leftIndicatorOpacity = Math.min(Math.max(-dragDelta / SWIPE_THRESHOLD, 0), 1);
  const rightIndicatorOpacity = Math.min(Math.max(dragDelta / SWIPE_THRESHOLD, 0), 1);

  const showSwipeHint = currentIndex === 0 && !isAnimatingOut && dragDelta === 0;

  return (
    <section className={styles.section} aria-label="Imperdibili">
      <div className={styles.headerRow}>
        <h2 className={styles.heading}>Imperdibili</h2>
        <span className={styles.counter}>
          {currentIndex + 1} / {events.length}
        </span>
      </div>

      <div className={styles.stackContainer}>
        {/* Render up to VISIBLE_STACK cards behind current */}
        {Array.from({ length: Math.min(remaining, VISIBLE_STACK) }).map((_, offset) => {
          const idx = currentIndex + offset;
          const event = events[idx];
          if (!event) return null;

          const isTop = offset === 0;

          // Fanned stack: behind cards offset to the right with slight rotation
          const stackOffsetX = offset * 20;
          const stackRotation = offset * 3;
          const stackScale = 1 - offset * 0.03;
          const stackOpacity = 1 - offset * 0.15;

          let transform: string;
          if (isTop) {
            if (isAnimatingOut && flyDirection) {
              const flyX = flyDirection === 'right' ? FLY_OFF_DISTANCE : -FLY_OFF_DISTANCE;
              const flyRotate = flyDirection === 'right' ? 20 : -20;
              transform = `translateX(${flyX}px) rotate(${flyRotate}deg)`;
            } else {
              transform = `translateX(${dragDelta}px) rotate(${rotation}deg)`;
            }
          } else {
            // If top card is animating out, the next card should promote forward
            const promoteProgress = isAnimatingOut && offset === 1 ? 1 : 0;
            const ox = stackOffsetX - promoteProgress * 20;
            const or = stackRotation - promoteProgress * 3;
            const os = stackScale + promoteProgress * 0.03;
            transform = `translateX(${ox}px) rotate(${or}deg) scale(${os})`;
          }

          const cardStyle: React.CSSProperties = {
            '--card-cat-color': categoryColors[event.category],
            transform,
            zIndex: VISIBLE_STACK - offset,
            opacity: isTop && isAnimatingOut ? 0.8 : stackOpacity,
            transition:
              isTop && !isAnimatingOut
                ? 'none'
                : `transform 300ms var(--ease-spring), opacity 300ms var(--ease-out)`,
          } as React.CSSProperties;

          // Link action (top card only): the pointer path already decided —
          // tap opened, drag did not — so only an activation no gesture
          // claimed (e.g. assistive tech firing a bare click) may navigate.
          const handleLinkClick = !isTop
            ? undefined
            : () => {
                if (swallowClickRef.current) {
                  swallowClickRef.current = false;
                  return;
                }
                navigate(`/event/${event.id}`, {
                  state: { backgroundLocation: location },
                });
              };

          return (
            <div
              key={event.id}
              ref={isTop ? cardRef : undefined}
              className={`${styles.card}${isTop ? ` ${styles.cardTop}` : ''}`}
              style={cardStyle}
              onPointerDown={isTop ? handlePointerDown : undefined}
              onPointerMove={isTop ? handlePointerMove : undefined}
              onPointerUp={isTop ? handlePointerUp : undefined}
              onPointerCancel={isTop ? handlePointerCancel : undefined}
              /* R17: no role="button"/tabIndex/aria-label on the container -
                 the title link is the interactive and focusable element.
                 The keydown handler stays: it fires when the link inside is
                 focused (arrow keys and Enter still advance the stack). */
              onKeyDown={
                isTop
                  ? (e) => {
                      if (e.key === 'ArrowRight' || e.key === 'Enter') {
                        e.preventDefault();
                        advanceCard('right');
                      } else if (e.key === 'ArrowLeft') {
                        e.preventDefault();
                        advanceCard('left');
                      }
                    }
                  : undefined
              }
            >
              {/* Event image: sponsor photo, then scraped image (https only), then category
                  fallback. Solo la prima carta della pila, se dichiarata visibile, è prioritaria. */}
              <EventCoverImage
                event={event}
                className={styles.coverImg}
                sizes={COVER_SIZES}
                priority={prioritizeFirstCard && idx === 0}
              />

              {/* Swipe indicator overlays (top card only) */}
              {isTop && !isAnimatingOut && (
                <>
                  <div
                    className={`${styles.swipeIndicator} ${styles.swipeLeft}`}
                    style={{ opacity: leftIndicatorOpacity }}
                    aria-hidden="true"
                  >
                    <span className={styles.swipeIcon}>&#10005;</span>
                    <span className={styles.swipeLabel}>Salta</span>
                  </div>
                  <div
                    className={`${styles.swipeIndicator} ${styles.swipeRight}`}
                    style={{ opacity: rightIndicatorOpacity }}
                    aria-hidden="true"
                  >
                    <span className={styles.swipeIcon}>&#10140;</span>
                    <span className={styles.swipeLabel}>Scopri</span>
                  </div>
                </>
              )}

              {/* Card content */}
              <div className={styles.cardContent}>
                <div className={styles.badgeRow}>
                  <span className={styles.categoryBadge}>
                    {categoryLabels[event.category] ?? event.category}
                  </span>
                </div>
                <h3 className={styles.title}>
                  <EventLink
                    eventId={event.id}
                    /* Non-top cards stay out of the tab order, as today. */
                    tabIndex={isTop ? undefined : -1}
                    onClick={handleLinkClick}
                    extended
                  >
                    {event.title}
                  </EventLink>
                </h3>
                <p className={styles.meta}>
                  {formatDateRange(event.startDate, event.endDate)}
                  {' \u00B7 '}
                  {event.location.city}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Swipe hint for first-time users. T105-FE: resta montato e si nasconde con visibility,
          così l'ingombro riservato sotto la pila è lo stesso in ogni stato e dopo il primo
          trascinamento il contenuto sotto non sale di ~45 px. */}
      <p
        className={styles.hint}
        aria-hidden="true"
        style={showSwipeHint ? undefined : { visibility: 'hidden' }}
      >
        &#8592; scorri per saltare &middot; scorri per scoprire &#8594;
      </p>
    </section>
  );
}
